import { reactive, computed } from 'vue';

const STORAGE_KEY = 'cpph_cookie_consent';

// Bump this when the cookie categories or policy change materially. Visitors
// who consented under an older version are asked again.
export const CONSENT_VERSION = 1;

export const CATEGORIES = {
  necessary: {
    key: 'necessary',
    label: 'Strictly necessary',
    readOnly: true,
    description:
      'Required for the site to load and for your cookie choice to be remembered. These cannot be switched off.',
  },
  analytics: {
    key: 'analytics',
    label: 'Analytics',
    readOnly: false,
    description:
      'Help us understand which pages and projects visitors look at, so we can improve the site. No analytics tool is active until you allow this.',
  },
  embeds: {
    key: 'embeds',
    label: 'Maps and embedded content',
    readOnly: false,
    description:
      'Allows the Google Maps view of our office to load. Google may set its own cookies when this content is displayed.',
  },
};

const defaultState = () => ({
  version: CONSENT_VERSION,
  decided: false,
  necessary: true,
  analytics: false,
  embeds: false,
  updatedAt: null,
});

const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return defaultState();
    return { ...defaultState(), ...parsed, decided: true };
  } catch (e) {
    // Private browsing or storage disabled. Treat as undecided and do not
    // block the site.
    return defaultState();
  }
};

export const consent = reactive(read());

const persist = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent }));
  } catch (e) {
    // Nothing to do. The choice simply will not survive a reload.
  }
};

const emit = () => {
  try {
    window.dispatchEvent(
      new window.CustomEvent('cookie-consent-change', { detail: { ...consent } })
    );
  } catch (e) {
    // CustomEvent unsupported. Reactive state still works in-app.
  }
};

/**
 * @param {object} choices e.g. { analytics: true, embeds: false }
 */
export const saveConsent = (choices = {}) => {
  Object.keys(CATEGORIES).forEach((key) => {
    if (CATEGORIES[key].readOnly) return;
    if (key in choices) consent[key] = Boolean(choices[key]);
  });
  consent.necessary = true;
  consent.decided = true;
  consent.version = CONSENT_VERSION;
  consent.updatedAt = new Date().toISOString();
  persist();
  emit();
};

export const acceptAll = () =>
  saveConsent({ analytics: true, embeds: true });

export const rejectOptional = () =>
  saveConsent({ analytics: false, embeds: false });

export const resetConsent = () => {
  Object.assign(consent, defaultState());
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
  emit();
};

export const hasConsent = (category) => Boolean(consent[category]);

export const useConsent = (category) => computed(() => Boolean(consent[category]));

/**
 * Reopens the preferences panel from anywhere, for example the footer link.
 * The banner component listens for this event.
 */
export const openCookiePreferences = () => {
  try {
    window.dispatchEvent(new window.CustomEvent('cookie-consent-open'));
  } catch (e) {
    // ignore
  }
};

export default consent;
